import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Ruler, ArrowRight, Clock, Check } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Kicker, WfButton, Tag, ImageWithCredit } from "@/components/site/primitives";
import {
  LANDSITES_HERO,
  LANDSITES_INTRO,
  LANDSITES,
  LANDSITES_STEWARDSHIP,
  LANDSITES_CTA,
} from "@/content/landsites";
import { cn } from "@/lib/utils";

/* ---------------- Intro ---------------- */
const IntroSection = () => (
  <section id="landsites-intro" aria-labelledby="landsites-intro-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-16">
        <Reveal>
          <Kicker>{LANDSITES_INTRO.eyebrow}</Kicker>
          <h2 id="landsites-intro-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            {LANDSITES_INTRO.title}
          </h2>
          <div className="mt-5 space-y-4">
            {LANDSITES_INTRO.paragraphs.map((p, i) => (
              <p key={i} className="max-w-[62ch] text-base leading-7 text-[var(--wf-soil)]">{p}</p>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <dl className="grid grid-cols-3 gap-3 lg:grid-cols-1">
            {LANDSITES_INTRO.stats.map((s) => (
              <div key={s.id} data-testid={`landsites-stat-${s.id}`} className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-5">
                <dd className="font-serif text-3xl font-semibold text-[var(--wf-maroon)] sm:text-4xl">{s.value}</dd>
                <dt className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--wf-green-text)] sm:text-sm">{s.label}</dt>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Landsite cards ---------------- */
const LandsiteCard = ({ site }) => {
  const body = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--wf-cream)]">
        <img
          src={site.image}
          alt={site.alt}
          loading="lazy"
          className={cn("h-full w-full object-cover transition-transform duration-[600ms] ease-out", site.placeholder ? "opacity-80" : "motion-safe:group-hover:scale-105")}
        />
        <span className="absolute left-4 top-4">
          {site.placeholder ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(255,253,248,0.92)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--wf-soil)] backdrop-blur-sm">
              <Clock className="h-3 w-3" aria-hidden="true" /> {site.status}
            </span>
          ) : (
            <Tag>{site.status}</Tag>
          )}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className={cn("font-serif text-2xl font-semibold leading-snug text-[var(--wf-ink)]", !site.placeholder && "group-hover:text-[var(--wf-maroon)]")}>
          {site.name}
        </h3>
        <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <dt className="sr-only">Location</dt>
            <MapPin className="h-4 w-4 text-[var(--wf-green-text)]" aria-hidden="true" />
            <dd>{site.town}</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <dt className="sr-only">Size</dt>
            <Ruler className="h-4 w-4 text-[var(--wf-green-text)]" aria-hidden="true" />
            <dd>{site.acres}</dd>
          </div>
        </dl>
        <p className="mt-4 flex-1 text-sm leading-6 text-[var(--wf-soil)]">{site.body}</p>
        <ul className="mt-4 flex flex-wrap gap-2" aria-label="Site features">
          {site.features.map((f) => (
            <li key={f} className="rounded-full border border-[var(--wf-border)] bg-[var(--wf-cream)] px-2.5 py-1 text-xs font-medium text-[var(--wf-soil)]">{f}</li>
          ))}
        </ul>
        {!site.placeholder && (
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--wf-maroon)]">
            Explore this site <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </span>
        )}
      </div>
    </>
  );

  const base = "flex h-full flex-col overflow-hidden rounded-sm border bg-[var(--wf-paper)]";
  return site.href ? (
    <Link
      to={site.href}
      data-testid={`landsite-card-${site.id}`}
      className={`${base} group border-[var(--wf-border)] shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background`}
    >
      {body}
    </Link>
  ) : (
    <div data-testid={`landsite-card-${site.id}`} aria-disabled="true" className={`${base} border-dashed border-[var(--wf-border)]`}>
      {body}
    </div>
  );
};

const SitesSection = () => (
  <section id="landsites" aria-labelledby="landsites-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <Kicker>Landsites</Kicker>
        <h2 id="landsites-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          Our growing sites
        </h2>
        <p className="mt-4 text-base leading-7 text-muted-foreground">
          Site details are being finalized — full descriptions, maps, and photos for each landsite will be added here.
        </p>
      </Reveal>
      <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="landsites-grid">
        {LANDSITES.map((s, i) => (
          <Reveal key={s.id} delay={0.06 * i} as="li" className="h-full">
            <LandsiteCard site={s} />
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------------- Stewardship ---------------- */
const StewardshipSection = () => (
  <section id="stewardship" aria-labelledby="landsites-stewardship-heading" className="relative overflow-hidden bg-[var(--wf-farm-green)] py-20 sm:py-24 lg:py-28">
    <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
    <div className="wf-container relative">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
        <Reveal>
          <ImageWithCredit
            src={LANDSITES_STEWARDSHIP.image}
            alt={LANDSITES_STEWARDSHIP.imageAlt}
            className="aspect-[4/5] w-full rounded-sm shadow-[var(--wf-shadow)] ring-1 ring-white/20"
            imgClassName="object-cover"
          />
        </Reveal>
        <Reveal delay={0.1}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">
            <span aria-hidden="true" className="h-px w-6 bg-[rgba(255,253,248,0.5)]" />
            {LANDSITES_STEWARDSHIP.eyebrow}
          </span>
          <h2 id="landsites-stewardship-heading" className="mt-5 font-serif text-3xl font-semibold leading-[1.15] text-white sm:text-4xl">
            {LANDSITES_STEWARDSHIP.title}
          </h2>
          <p className="mt-6 max-w-[60ch] text-base leading-7 text-[rgba(255,253,248,0.9)]">{LANDSITES_STEWARDSHIP.body}</p>
          <ul className="mt-6 space-y-3">
            {LANDSITES_STEWARDSHIP.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-[rgba(255,253,248,0.92)]">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
                  <Check className="h-3 w-3" aria-hidden="true" />
                </span>
                <span className="text-sm leading-6 sm:text-base">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- CTA ---------------- */
const CtaSection = () => (
  <section aria-labelledby="landsites-cta-heading" className="bg-[var(--wf-cream)] py-16 sm:py-20 lg:py-24">
    <div className="wf-container">
      <div className="relative overflow-hidden rounded-sm bg-[var(--wf-maroon)] px-6 py-14 text-center sm:px-10 sm:py-16">
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--wf-clay)]/30 blur-2xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-[var(--wf-wheat)]/20 blur-2xl" />
        <div className="relative mx-auto max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">{LANDSITES_CTA.eyebrow}</span>
          <h2 id="landsites-cta-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">{LANDSITES_CTA.title}</h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.85)]">{LANDSITES_CTA.body}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <WfButton as={Link} to={LANDSITES_CTA.primary.href} variant="light" withArrow className="px-7" data-testid="landsites-cta-contact">{LANDSITES_CTA.primary.label}</WfButton>
            <WfButton as={Link} to={LANDSITES_CTA.secondary.href} className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="landsites-cta-mentorship">{LANDSITES_CTA.secondary.label}</WfButton>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Page ---------------- */
export default function ProgramLandsitesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...LANDSITES_HERO} testid="landsites-hero" />
        <IntroSection />
        <SitesSection />
        <StewardshipSection />
        <CtaSection />
      </main>
      <SiteFooter />
    </>
  );
}
